import { ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideAppInitializer, inject } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDa from '@angular/common/locales/da';
import localeDe from '@angular/common/locales/de';
import localeEnGb from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import localeSv from '@angular/common/locales/sv';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { TranslateLoader, MissingTranslationHandler, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { VestfrostPreset } from './core/theme/vestfrost.theme';
import { MyMissingTranslationHandler } from './core/missing-translation.handler';
import { AuthService } from './core/services/auth.service';
import { PermissionsService } from './core/services/permissions.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { routes } from './app.routes';

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

/**
 * The per-response CSP nonce, read back from the `ngCspNonce` attribute nginx stamped onto `<app-root>`.
 *
 * Angular consumes that attribute itself for the styles *it* injects, but PrimeNG injects its own and
 * needs the value handed to it explicitly — so it is read from the DOM rather than duplicated into a
 * second placeholder that could drift. Returns `null` under `ng serve`, where nginx never runs and the
 * literal placeholder is still in the markup; there is no CSP there either, so nothing needs a nonce.
 */
function cspNonce(): string | null {
  const raw = document.querySelector('app-root')?.getAttribute('ngCspNonce');
  return raw !== null && raw !== undefined && raw !== '' && raw !== 'REPLACE_NONCE' ? raw : null;
}

/**
 * Locales we ship CLDR data for, covering the markets in the fleet (DK, DE, FR, NL, SE, GB).
 *
 * Number and date formatting follows the **browser's** locale, deliberately independent of the UI
 * language: a Dane browsing in English still sees `12,2` and `7. aug. 2026`, because that is how
 * their machine is configured. Angular needs data registered per locale, so a browser locale
 * outside this list falls back to `en-US` (Angular's built-in). Add an import here to cover another
 * market — each file is only ~2 kB.
 */
const SUPPORTED_LOCALES: readonly string[] = ['da', 'de', 'en-GB', 'fr', 'nl', 'sv'];
[localeDa, localeDe, localeEnGb, localeFr, localeNl, localeSv].forEach(l => registerLocaleData(l));

/**
 * BCP-47 tag for `LOCALE_ID`, resolved from the browser. Angular binds LOCALE_ID once at bootstrap,
 * which is fine here — the browser's locale cannot change mid-session, unlike the UI language.
 */
export function browserLocaleFactory(): string {
  const preferred = navigator.language ?? '';
  // Exact region match first ("en-GB"), then the bare language ("da-DK" → "da"), then the built-in.
  if (SUPPORTED_LOCALES.includes(preferred)) return preferred;
  // `split` always yields at least one element, but the compiler cannot know that; `''` matches no
  // supported locale, so an empty tag falls through to the built-in exactly as an unknown one does.
  const base = preferred.split('-')[0] ?? '';
  return SUPPORTED_LOCALES.includes(base) ? base : 'en-US';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: LOCALE_ID, useFactory: browserLocaleFactory },
    provideRouter(routes),
    provideAnimationsAsync(),
    provideOAuthClient(),
    provideHttpClient(withInterceptors([authInterceptor])),
    // One initializer, two steps, chained rather than registered separately: Angular starts all
    // initializers together, and the permissions lookup needs a token — which only exists once
    // `AuthService.init()` has resolved. Both services are captured before the first `await`,
    // because `inject()` is only legal inside the synchronous injection context.
    provideAppInitializer(() => {
      const auth        = inject(AuthService);
      const permissions = inject(PermissionsService);
      return auth.init().then(() => permissions.init());
    }),
    provideTranslateService({
      defaultLanguage: 'da',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
      },
    }),
    providePrimeNG({
      theme: {
        preset: VestfrostPreset,
        options: { darkModeSelector: '.dark-mode', cssLayer: false }
      },
      // PrimeNG injects its own <style> elements, which a nonce-based `style-src` blocks unless they
      // carry the same nonce Angular uses. Without this the theme silently disappears under the CSP.
      csp: { nonce: cspNonce() ?? undefined }
    })
  ]
};
